import EventEmitter from "events";

/**
 * 순수 WebSocket 관리자 (비즈니스 로직 없음)
 * shared 레이어에 위치하여 어디서든 재사용 가능
 */

export interface WebSocketConfig {
  url: string;
  autoReconnect?: boolean;
  maxRetries?: number;
  reconnectDelay?: number;
}

export type ConnectionState = "connecting" | "connected" | "disconnecting" | "disconnected";

export class WebSocketManager extends EventEmitter {
  private socket: WebSocket | null = null;
  private config: WebSocketConfig;
  private retryCount: number = 0;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private _state: ConnectionState = "disconnected";
  private connectionGeneration: number = 0;
  private pendingConnectReject: ((reason?: unknown) => void) | null = null;

  constructor(config: WebSocketConfig) {
    super();
    this.config = {
      autoReconnect: true,
      maxRetries: 5,
      reconnectDelay: 1000,
      ...config,
    };
  }

  /**
   * 현재 연결 상태
   */
  get state(): ConnectionState {
    return this._state;
  }

  get isConnected(): boolean {
    return this._state === "connected" && this.socket?.readyState === WebSocket.OPEN;
  }

  get hasPendingReconnect(): boolean {
    return this.reconnectTimeout !== null;
  }

  /**
   * WebSocket 연결 시작
   */
  async connect(): Promise<void> {
    if (this._state === "connecting" || this._state === "connected") {
      console.warn("⚠️ 이미 연결 중이거나 연결되어 있습니다");
      return;
    }

    this._state = "connecting";
    this.emit("state_change", this._state);
    const generation = ++this.connectionGeneration;

    return new Promise((resolve, reject) => {
      try {
        const socket = new WebSocket(this.config.url);
        this.socket = socket;
        this.pendingConnectReject = reject;

        socket.onopen = () => {
          if (!this.isCurrentConnection(socket, generation)) return;
          console.log("✅ WebSocket 연결 성공:", this.config.url);
          this._state = "connected";
          this.retryCount = 0;
          this.pendingConnectReject = null;
          this.emit("state_change", this._state);
          this.emit("open");
          resolve();
        };

        socket.onclose = (event) => {
          if (!this.isCurrentConnection(socket, generation)) return;
          console.log("🔌 WebSocket 연결 종료:", event.code, event.reason);
          const wasConnecting = this._state === "connecting";
          const rejectPendingConnect = this.pendingConnectReject;
          this.socket = null;
          this.pendingConnectReject = null;
          this._state = "disconnected";
          this.emit("state_change", this._state);
          this.emit("close", { code: event.code, reason: event.reason });
          if (wasConnecting) {
            rejectPendingConnect?.(new Error(
              `WebSocket closed before opening: ${event.reason || event.code}`,
            ));
          }

          // 자동 재연결
          if (this.config.autoReconnect && event.code !== 1000) {
            this.scheduleReconnect(generation);
          }
        };

        socket.onmessage = (event) => {
          if (!this.isCurrentConnection(socket, generation)) return;
          try {
            const data = JSON.parse(event.data);
            console.log("📩 [WS_MANAGER] 메시지 수신:", {
              type: data?.type,
              hasType: !!data?.type,
              dataKeys: data ? Object.keys(data) : []
            });

            // 메시지 타입별로 이벤트 발생
            if (data && typeof data === "object" && data.type) {
              console.log(`🔔 [WS_MANAGER] 이벤트 emit:`, {
                eventName: data.type,
                listenerCount: this.listenerCount(data.type)
              });

              this.emit("message", data);
              this.emit(data.type, data); // 타입별 이벤트

              console.log(`✅ [WS_MANAGER] 이벤트 emit 완료: ${data.type}`);
            }
          } catch (error) {
            console.error("❌ 메시지 파싱 실패:", error);
            this.emit("parse_error", { rawData: event.data, error });
          }
        };

        socket.onerror = (error) => {
          if (!this.isCurrentConnection(socket, generation)) return;
          console.error("❌ WebSocket 에러:", error);
          this.emit("transport_error", error);

          const wasConnecting = this._state === "connecting";
          const rejectPendingConnect = this.pendingConnectReject;

          // 실패한 transport는 onclose를 기다리지 않고 직접 폐기한다. 늦은 close
          // 콜백이 중복 재연결을 예약하지 못하도록 먼저 리스너를 분리한다.
          this.detachSocket(socket);
          if (
            socket.readyState === WebSocket.CONNECTING
            || socket.readyState === WebSocket.OPEN
          ) {
            socket.close();
          }
          this.socket = null;
          this.pendingConnectReject = null;
          this._state = "disconnected";
          this.emit("state_change", this._state);
          this.emit("close", { code: 1006, reason: "transport_error" });

          if (wasConnecting) rejectPendingConnect?.(error);
          if (this.config.autoReconnect) this.scheduleReconnect(generation);
        };
      } catch (error) {
        console.error("❌ WebSocket 생성 실패:", error);
        this.pendingConnectReject = null;
        this._state = "disconnected";
        this.emit("state_change", this._state);
        this.emit("transport_error", error);
        if (this.config.autoReconnect) this.scheduleReconnect(generation);
        reject(error);
      }
    });
  }

  /**
   * 재연결 스케줄링
   */
  private scheduleReconnect(generation: number): void {
    if (this.reconnectTimeout) return;

    if (this.retryCount >= (this.config.maxRetries ?? 5)) {
      console.error("❌ 최대 재시도 횟수 초과");
      this.emit("max_retries_exceeded", { retryCount: this.retryCount });
      return;
    }

    this.retryCount++;
    console.log(`🔄 재연결 시도 ${this.retryCount}/${this.config.maxRetries}`);
    this.emit("retry_attempt", { retryCount: this.retryCount, maxRetries: this.config.maxRetries });

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      if (generation !== this.connectionGeneration || this._state !== "disconnected") {
        return;
      }
      this.connect().catch((error) => {
        console.error("❌ 재연결 실패:", error);
      });
    }, this.config.reconnectDelay);
  }

  /**
   * 메시지 전송
   */
  send<T = Record<string, unknown>>(message: T): boolean {
    if (!this.isConnected) {
      console.error("❌ WebSocket이 연결되지 않았습니다");
      return false;
    }

    try {
      this.socket!.send(JSON.stringify(message));
      return true;
    } catch (error) {
      console.error("❌ 메시지 전송 실패:", error);
      return false;
    }
  }

  /**
   * WebSocket 연결 종료
   */
  disconnect(): void {
    this.connectionGeneration++;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.socket) {
      const socket = this.socket;
      this._state = "disconnecting";
      this.emit("state_change", this._state);

      // 이벤트 리스너 제거
      this.detachSocket(socket);

      // 정상 종료
      if (
        socket.readyState === WebSocket.CONNECTING ||
        socket.readyState === WebSocket.OPEN
      ) {
        socket.close(1000, "Normal closure");
      }

      this.socket = null;
    }

    const rejectPendingConnect = this.pendingConnectReject;
    this.pendingConnectReject = null;
    rejectPendingConnect?.(new Error("WebSocket connection cancelled"));

    this._state = "disconnected";
    this.emit("state_change", this._state);
  }

  private isCurrentConnection(socket: WebSocket, generation: number): boolean {
    return this.socket === socket && this.connectionGeneration === generation;
  }

  private detachSocket(socket: WebSocket): void {
    socket.onopen = null;
    socket.onclose = null;
    socket.onmessage = null;
    socket.onerror = null;
  }

  /**
   * 재시도 카운트 초기화
   */
  resetRetryCount(): void {
    this.retryCount = 0;
  }

  /**
   * 자동 재연결 설정
   */
  setAutoReconnect(enabled: boolean): void {
    this.config.autoReconnect = enabled;
    if (!enabled && this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  /**
   * 리소스 정리 (더 이상 사용하지 않을 때)
   */
  destroy(): void {
    this.disconnect();
    this.removeAllListeners();
  }
}
